<template>
	<div class="links-icons">
		<ArkHoverCard
			v-for="link in links"
			:key="link.id"
			:positioning="{ placement: 'bottom', gutter: 4 }"
			:open-delay="200"
			:close-delay="100"
		>
			<template #trigger>
				<component
					:is="link.href ? 'a' : 'button'"
					class="icon-button"
					:href="link.href"
					:target="link.href ? link.target ?? '_blank' : undefined"
					:type="!link.href ? 'button' : undefined"
					:aria-label="link.label"
					@click="handleSelect(link)"
				>
					<AppIcon v-if="link.icon === 'link'" :name="IconName.LinkChain" :size="18" :stroke-width="1.8" />
					<AppIcon v-else-if="link.icon === 'map-pin'" :name="IconName.MapPin" :size="18" :stroke-width="1.8" />
					<AppIcon
						v-else-if="link.icon === 'file-signature'"
						:name="IconName.FileText"
						:size="18"
						:stroke-width="1.8"
					/>
					<AppIcon v-else-if="link.icon === 'archive'" :name="IconName.Archive" :size="21" :stroke-width="1.6" />
					<img
						v-else-if="link.icon === 'giraffe'"
						class="giraffe-icon"
						:src="giraffeIconUrl"
						width="18"
						height="18"
						alt=""
						aria-hidden="true"
					/>
					<AppIcon v-else-if="link.icon === 'download'" :name="IconName.Download" :size="18" :stroke-width="1.8" />
					<AppIcon v-else-if="link.icon === 'hubspot'" :name="IconName.Hubspot" :size="18" />
					<AppIcon v-else :name="IconName.ExternalLink" :size="18" :stroke-width="1.8" />
				</component>
			</template>
			<div class="tooltip-content">{{ link.label }}</div>
		</ArkHoverCard>
	</div>
</template>

<script setup lang="ts">
import ArkHoverCard from "@/components/ui/ArkHoverCard.vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import giraffeIconUrl from "@/assets/images/giraffe-icon.png"

type EntityLinkIcon =
	| "link"
	| "map-pin"
	| "external-link"
	| "download"
	| "hubspot"
	| "file-signature"
	| "giraffe"
	| "archive"

export interface EntityLink {
	id: string
	label: string
	icon?: EntityLinkIcon
	href?: string
	target?: "_blank" | "_self"
	onClick?: () => void
}

defineProps<{
	links: EntityLink[]
}>()

const emit = defineEmits<{
	select: [id: string]
}>()

function handleSelect(link: EntityLink): void {
	emit("select", link.id)
	link.onClick?.()
}
</script>

<style scoped>
.links-icons {
	display: flex;
	gap: var(--spacing-xs);
}

.icon-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-base);
	background-color: var(--neutral-0);
	color: var(--neutral-600);
	cursor: pointer;
	transition:
		background-color 0.2s ease,
		border-color 0.2s ease,
		color 0.2s ease;

	&:hover {
		background-color: var(--neutral-100);
		border-color: var(--neutral-400);
		color: var(--neutral-900);
	}

	&:focus-visible {
		outline: 2px solid var(--neutral-600);
		outline-offset: 2px;
	}
}

.giraffe-icon {
	filter: grayscale(1);
	opacity: 0.65;
}

.tooltip-content {
	padding: var(--spacing-xs) var(--spacing-md);
	font-size: var(--font-size-small);
	color: var(--neutral-900);
	white-space: nowrap;
}
</style>
